import React, { useState } from 'react'
import { Clipboard, LogIn } from 'lucide-react'

interface JoinPrivateGameModalProps {
  onClose: () => void
  onGameJoined: (gameNumber: number, password: string) => void
}

// AGC: Join Private Game Modal - Paste game code to join
export const JoinPrivateGameModal: React.FC<JoinPrivateGameModalProps> = ({
  onClose,
  onGameJoined,
}) => {
  const [gameNumber, setGameNumber] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [error, setError] = useState<string>('')

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      
      // AGC: Parse "number#password" format
      if (text.includes('#')) {
        const [num, pass] = text.split('#')
        const parsedNum = parseInt(num.trim(), 10)
        
        if (isNaN(parsedNum)) {
          setError('Invalid game code format')
          return
        }
        
        setGameNumber(parsedNum.toString())
        setPassword(pass.trim())
        setError('')
      } else {
        setError('Invalid game code format. Expected: number#password')
      }
    } catch (error) {
      console.error('[JoinPrivateGame] Failed to paste:', error)
      setError('Failed to paste from clipboard')
    }
  }

  const handleJoin = () => {
    const num = parseInt(gameNumber, 10)
    
    if (isNaN(num) || num < 1000) {
      setError('Invalid game number')
      return
    }
    
    if (!password || password.length < 5) {
      setError('Invalid password')
      return
    }
    
    onGameJoined(num, password)
  }

  const isValid = gameNumber && password && !error

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="w-full max-w-sm bg-slate-950 border border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* AGC: Header */}
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="text-base font-semibold text-white">Join Private Game</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Enter game code or paste from clipboard</p>
        </div>

        {/* AGC: Content */}
        <div className="px-4 py-4 space-y-3">
          {/* AGC: Paste Button */}
          <button
            onClick={handlePaste}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-400/40 rounded-xl hover:from-blue-600/30 hover:to-purple-600/30 transition-colors"
          >
            <Clipboard className="w-4 h-4 text-blue-300" />
            <span className="text-sm font-semibold text-blue-300">Paste Game Code</span>
          </button>

          {/* AGC: Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-slate-800"></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">OR ENTER MANUALLY</span>
            <div className="flex-1 h-px bg-slate-800"></div>
          </div>

          {/* AGC: Game Number Input */}
          <div>
            <label className="block text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">
              GAME NUMBER
            </label>
            <input
              type="text"
              value={gameNumber}
              onChange={(e) => {
                setGameNumber(e.target.value.replace(/\D/g, '')) // Only digits
                setError('')
              }}
              placeholder="e.g. 1234"
              className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-800 rounded-lg text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 transition-colors"
            />
          </div>

          {/* AGC: Password Input */}
          <div>
            <label className="block text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">
              PASSWORD
            </label>
            <input
              type="text"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')) // Only alphanumeric
                setError('')
              }}
              placeholder="e.g. ABC12"
              maxLength={5}
              className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-800 rounded-lg text-white text-sm tracking-[0.3em] placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50 transition-colors"
            />
          </div>

          {/* AGC: Preview Combined Code */}
          {gameNumber && password && (
            <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">
              <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1">GAME CODE</p>
              <p className="text-base font-mono font-bold text-white">{gameNumber}#{password}</p>
            </div>
          )}

          {/* AGC: Error Message */}
          {error && (
            <div className="bg-red-600/20 border border-red-400/40 rounded-lg p-2.5">
              <p className="text-[11px] text-red-300">{error}</p>
            </div>
          )}

          {/* AGC: Info */}
          <div className="bg-slate-900/40 rounded-lg p-2.5">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Paste the game code received from your friend or enter the game number and password manually.
            </p>
          </div>
        </div>

        {/* AGC: Actions */}
        <div className="px-4 py-3 border-t border-white/10 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700 text-sm text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleJoin}
            disabled={!isValid}
            className={`flex-1 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-all ${
              isValid
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/30'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed'
            }`}
          >
            <LogIn className="w-4 h-4" />
            Join Game
          </button>
        </div>
      </div>
    </div>
  )
}

